import { useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useSetAtom } from 'jotai';

import { SystemPlan } from '@cfa/pathway-fe-shared/xp';

import { addSortFields } from '@/hooks/useSortOptions';
import useSystemPlans from '@/hooks/useSystemPlans';

import { isApiError } from '@/utils/request';
import { cn } from '@/utils/tailwind';

import GenericError from '@/components/Error/GenericError';
import { PathwayModal } from '@/components/popups/PathwayModal';

import ChooseTemplateModalStep from './ChooseTemplateModalStep';
import CreateOrCopyPlanModalStep from './CreateOrCopyPlanModalStep';
import PreviewTemplateModalStep from './PreviewTemplateModalStep';

import {
  DataViewAtomFamilyKey,
  resetDataViewFamily,
  sourceDataFamily,
} from '@/atoms/data-view';
import { useAddReadOnlyPlan } from '@/routes/TrainingPlans/hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';

interface AddTemplateModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const STEP = {
  TEMPLATES: 'templates',
  PREVIEW: 'preview',
  COPY: 'copy',
  COMPLIANCE: 'compliance',
  INVALID_NAME: 'invalidName',
} as const;

type StepKey = (typeof STEP)[keyof typeof STEP];

const DATA_VIEW_KEY = DataViewAtomFamilyKey.CHOOSE_TEMPLATE;

const AddTemplateModal: React.FC<AddTemplateModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [previewTemplate, setPreviewTemplate] = useState<SystemPlan | null>(
    null,
  );
  const [showPreview, setShowPreview] = useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const [copiedTemplateId, setCopiedTemplateId] = useState('');
  const [showCopyPlan, setShowCopyPlan] = useState(false);

  const {
    systemPlans: templates,
    systemPlansError: templatesError,
    isPendingSystemPlans,
  } = useSystemPlans();

  const setSource = useSetAtom(sourceDataFamily(DATA_VIEW_KEY));
  const resetDataView = useSetAtom(resetDataViewFamily(DATA_VIEW_KEY));

  const {
    subscribeReadonlyPlan,
    isPending,
    pendingPlanId,
    complianceModal,
    complianceModalIsOpen,
    onComplianceModalClose,
  } = useAddReadOnlyPlan({});

  useEffect(() => {
    setSource(templates.map(addSortFields));
  }, [templates, setSource]);

  useEffect(() => {
    if (!isOpen) {
      resetDataView();
    }
    return () => {
      resetDataView();
    };
  }, [isOpen, resetDataView]);

  const handleAddFromPreview = (template: SystemPlan) => {
    if (template.category === 'compliance' && template.readonly) {
      setShowPreview(false);
    }
    subscribeReadonlyPlan(template);
  };

  if (isApiError(templatesError)) {
    datadogRum.addError(
      templatesError instanceof Error
        ? templatesError
        : new Error(JSON.stringify(templatesError)),
    );
    return <GenericError />;
  }

  const handlePreviewPlan = (template: SystemPlan) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleCancel = () => {
    setCopiedTemplateId('');
    setShowCopyPlan(false);
    setPreviewTemplate(null);
    setShowPreview(false);
    setShowInvalidNameError(false);
  };

  const handleClose = () => {
    if (showInvalidNameError) {
      setShowInvalidNameError(false);
      return;
    }
    setCopiedTemplateId('');
    setPreviewTemplate(null);
    setShowPreview(false);
    setShowCopyPlan(false);
    resetDataView();
    setIsOpen(false);
    onComplianceModalClose();
  };

  const onShowCopyPlan = (id: string) => {
    setCopiedTemplateId(id);
    setShowPreview(false);
    setShowCopyPlan(true);
  };

  const handleCopyTemplateId = (id: string) => {
    setCopiedTemplateId(id);
    setShowCopyPlan(true);
  };

  const step: StepKey = complianceModalIsOpen
    ? STEP.COMPLIANCE
    : showPreview
      ? STEP.PREVIEW
      : showCopyPlan
        ? showInvalidNameError
          ? STEP.INVALID_NAME
          : STEP.COPY
        : STEP.TEMPLATES;

  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={handleClose}
      stepKey={step}
      className={cn('transition-all', {
        'w-full! xl:max-w-[969px]! 2xl:max-w-[969px]!': step === STEP.TEMPLATES,
        'w-full! xl:max-w-[840px]! 2xl:max-w-[840px]!': step === STEP.PREVIEW,
        'w-full! xl:max-w-[610px]! 2xl:max-w-[610px]!':
          step === STEP.COPY || step === STEP.INVALID_NAME,
      })}
      fullHeight={step === STEP.TEMPLATES}
    >
      <PathwayModal.Step stepKey={STEP.COMPLIANCE}>
        {complianceModal}
      </PathwayModal.Step>

      <PathwayModal.Step stepKey={STEP.TEMPLATES}>
        <ChooseTemplateModalStep
          onAdd={subscribeReadonlyPlan}
          subscribingTemplateId={pendingPlanId}
          isPending={isPending}
          isLoadingTemplates={isPendingSystemPlans}
          onCopy={handleCopyTemplateId}
          onPreviewTemplateClick={handlePreviewPlan}
        />
      </PathwayModal.Step>

      <PathwayModal.Step stepKey={STEP.PREVIEW}>
        {previewTemplate && (
          <PreviewTemplateModalStep
            onCopy={onShowCopyPlan}
            onTemplateBackButtonClick={handleCancel}
            plan={previewTemplate}
            onAdd={handleAddFromPreview}
            isLoading={isPending && pendingPlanId === previewTemplate.id}
          />
        )}
      </PathwayModal.Step>

      <PathwayModal.Step stepKey={showCopyPlan ? step : STEP.COPY}>
        <CreateOrCopyPlanModalStep
          copiedTemplateId={copiedTemplateId}
          onCancel={handleCancel}
          setShowInvalidNameError={setShowInvalidNameError}
          showInvalidNameError={showInvalidNameError}
        />
      </PathwayModal.Step>
    </PathwayModal>
  );
};

export default AddTemplateModal;
